import React from "react";
import { Chip, ChipProps, Tooltip } from "@mui/material";
import { components } from "../../types/api";
import { getStatusLabel } from "../../utils/briefingPermissions";

type BriefingStatus = components["schemas"]["BriefingStatus"];
type AssociatedCampaignStatus =
  components["schemas"]["_36_Enums.AssociatedCampaignStatus"];

export const BRIEFING_STATUS_COLORS: Record<
  BriefingStatus,
  ChipProps["color"]
> = {
  DRAFT: "default",
  READY_FOR_PRICING: "warning",
  READY_FOR_PRODUCTION: "info",
  IN_PRODUCTION: "secondary",
  FINALIZED: "success",
  UPDATING: "primary",
  ARCHIVED: "error",
};

export const CAMPAIGN_STATUS_COLORS: Record<
  AssociatedCampaignStatus,
  ChipProps["color"]
> = {
  DRAFT: "default",
  PENDING: "warning",
  READY: "info",
  ACTIVE: "secondary",
  FINALIZED: "success",
};

export const CAMPAIGN_STATUS_LABELS: Record<AssociatedCampaignStatus, string> =
  {
    DRAFT: "Draft",
    PENDING: "Pending",
    READY: "Ready",
    ACTIVE: "Active",
    FINALIZED: "Finalized",
  };

export const CAMPAIGN_STATUS_DESCRIPTIONS: Record<
  AssociatedCampaignStatus,
  string
> = {
  DRAFT: "Borrador: la campaña aún no está lista.",
  PENDING: "Pausada: excluida del recálculo automático hasta reanudarla.",
  READY: "Lista: a la espera de comenzar a recibir métricas.",
  ACTIVE:
    "Recibiendo métricas reales de la plataforma, dentro de las fechas del Goal.",
  FINALIZED: "Pasó la fecha de fin del Goal; no se esperan más datos.",
};

interface BriefingStatusChipProps extends Omit<ChipProps, "color" | "label"> {
  status: BriefingStatus;
}

export const BriefingStatusChip: React.FC<BriefingStatusChipProps> = ({
  status,
  ...rest
}) => {
  return (
    <Chip
      label={getStatusLabel(status)}
      color={BRIEFING_STATUS_COLORS[status] || "default"}
      size="small"
      {...rest}
    />
  );
};

interface CampaignStatusChipProps extends Omit<ChipProps, "color" | "label"> {
  status: AssociatedCampaignStatus;
}

export const CampaignStatusChip: React.FC<CampaignStatusChipProps> = ({
  status,
  ...rest
}) => {
  return (
    <Tooltip title={CAMPAIGN_STATUS_DESCRIPTIONS[status] ?? ""}>
      <Chip
        label={CAMPAIGN_STATUS_LABELS[status] ?? status}
        color={CAMPAIGN_STATUS_COLORS[status] || "default"}
        size="small"
        sx={{ textTransform: "capitalize", ...rest.sx }}
        {...rest}
      />
    </Tooltip>
  );
};
